import React, { useEffect, useMemo, useRef } from "react";

import { milliseconds } from "date-fns";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import useGetStatusTransitionProposalOcrDocs from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetStatusTransitionProposalOcrDocs";
import { getTransitionProposalsKeyMaker } from "@fartherfinance/frontend/api/TransitionProposals/hooks/useGetTransitionProposals";
import {
  StatusResponseBody,
  type TransitionProposalId,
  TransitionProposalsStatusMap,
} from "@fartherfinance/frontend/api/TransitionProposals/types";
import { ClientId, HookResult } from "@fartherfinance/frontend/api/Types";

import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import { useGlobalPollingContext } from "@src/multiCustodian/providers/GlobalPolling/GlobalPollingProvider";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import Chip from "@src/sharedComponents/Chip/Chip";

interface Props {
  transitionProposalId: TransitionProposalId;
}

const TransitionProposalChipStatusProcessingPolling = ({
  transitionProposalId,
}: Props) => {
  const { clientId } = useParams<{ clientId: ClientId }>();

  const auth = useRequestAuth();
  const { startPolling, stopPolling, isPollingSessionActive } =
    useGlobalPollingContext();

  const hasStartedPolling = useRef(false);

  const dispatch = useDispatch();

  const handleOnSuccess = ({ data }: HookResult<StatusResponseBody>) => {
    stopPolling<TransitionProposalId>(transitionProposalId);

    if (!data?.data) {
      return;
    }

    const processedTransitionProposalId = data.data;

    // Track status change from "processing" to "processed"
    trackEvent({
      name: "Advisor Change Transition-Proposal Status",
      properties: {
        proposalId: transitionProposalId,
        oldStatus: "processing",
        newStatus: "processed",
      },
    });

    // Also track the processing completed event
    trackEvent({
      name: "Advisor Process Transition-Proposal",
      properties: {
        proposalId: transitionProposalId,
      },
    });

    dispatch({
      type: "SET_GLOBAL_ALERT_DATA",
      payload: {
        message: "Your data is ready! Finish your proposal now.",
        severity: "info",
        variant: "filled",
        buttonText: "View Proposal",
        to: `/Client/${clientId}/Investments/TransitionProposals/${processedTransitionProposalId}`,
        showIcon: false,
      },
    });
  };

  const handleOnError = () => {
    stopPolling<TransitionProposalId>(transitionProposalId);

    dispatch({
      type: "SET_GLOBAL_ALERT_DATA",
      payload: {
        message: "There was an error processing your data.",
        severity: "error",
      },
    });
  };

  const isPollingActive = useMemo(
    () => isPollingSessionActive(transitionProposalId),
    [isPollingSessionActive, transitionProposalId]
  );

  useEffect(() => {
    if (!hasStartedPolling.current && !isPollingActive) {
      hasStartedPolling.current = true;

      startPolling<
        TransitionProposalId,
        StatusResponseBody,
        typeof useGetStatusTransitionProposalOcrDocs
      >(
        transitionProposalId,
        {
          useQueryHook: useGetStatusTransitionProposalOcrDocs,
          hookParams: [
            transitionProposalId,
            auth,
            false,
            {
              refetchInterval: milliseconds({ seconds: 5 }),
              refetchIntervalInBackground: true,
            },
          ],
          queryKey: getTransitionProposalsKeyMaker(null),
        },
        handleOnSuccess,
        handleOnError
      );
    }

    return () => {
      if (!isPollingActive) {
        hasStartedPolling.current = false;
      }
    };
    // In this case, we only want startPolling to run once.
    // Once the poll kicks off, the useGlobalPollingContext take control of the polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Chip label={TransitionProposalsStatusMap.processing} variant="outlined" />
  );
};

export default TransitionProposalChipStatusProcessingPolling;
